import { ApolloClient, ApolloError, useApolloClient } from '@apollo/client'
import { useEffect, useMemo, useState } from 'react'
import { FieldworkV2Query } from '../data/gql-gen/questionnaire/graphql'
import { GET_FIELDWORK } from '../data/gql/questionnaire/queries/fieldwork'
import { LoggerErrorType } from '../data/logger'
import { captureApolloError } from '../utils/HelperFunctions'

type Fieldwork = FieldworkV2Query['fieldwork']

/**
 * The `language` field's contract on the fd-core-responses results, filters and
 * quotas endpoints: a BCP-47 code, sent verbatim. A locale string (`de_DE`) is
 * rejected with a 400, so every code is normalised and checked against this
 * before it can be sent.
 */
const LANGUAGE_CODE_PATTERN = /^[a-z]{2,3}(-[A-Za-z0-9]{2,8})?$/

export interface ResultsLanguages {
  /** The language results are resolved in by default, or undefined if unknown. */
  primaryLanguage: string | undefined
  /**
   * Every language the results can be requested in, primary first. Empty until
   * the fieldwork resolves, and stays empty when it fails — callers then omit
   * the `language` field and get the current (unlocalised) behaviour.
   */
  availableLanguages: string[]
  /**
   * True from the first render until the fieldwork settles. Callers must gate
   * their results requests on this: firing before the language is known sends an
   * unlocalised request that a second, localised one immediately replaces,
   * flashing English question text.
   */
  loading: boolean
}

interface MarketLanguages {
  primary: string | undefined
  languages: string[]
}

const toLanguageCode = (
  value: string | null | undefined
): string | undefined => {
  const code = value?.trim().replace(/_/g, '-')
  return code && LANGUAGE_CODE_PATTERN.test(code) ? code : undefined
}

/**
 * A market's languages, dominant allocation first, falling back to its locale
 * language for single-language and legacy markets that allocate nothing.
 *
 * Unlike the audience builder's switch (deriveMarketLanguages) English is *not*
 * appended: these are the languages results are actually requested in, so
 * offering a language the market was never fielded in would ask
 * fd-core-responses to resolve text that does not exist — and would put a
 * dropdown on a single-language survey, which must localise silently.
 */
const deriveMarketLanguages = (fieldwork: Fieldwork): MarketLanguages => {
  // Array.sort is stable, so equal-percent languages keep their backend order.
  const languages = [...(fieldwork?.languageAllocations ?? [])]
    .sort((a, b) => b.percent - a.percent)
    .map((allocation) => toLanguageCode(allocation.language))
    .filter((code): code is string => code !== undefined)

  const localeLanguage = toLanguageCode(fieldwork?.locale.language)
  if (localeLanguage && !languages.includes(localeLanguage)) {
    languages.push(localeLanguage)
  }

  return { primary: languages[0], languages }
}

const fetchMarketLanguages = async (
  client: ApolloClient<unknown>,
  surveyId: string
): Promise<MarketLanguages | undefined> => {
  try {
    const { data } = await client.query({
      query: GET_FIELDWORK,
      variables: { surveyId },
      context: { clientName: 'questionnaire' },
      // The per-market results page usually has this in the cache already, and
      // the combined view re-reads it on every market switch.
      fetchPolicy: 'cache-first'
    })
    return deriveMarketLanguages(data.fieldwork)
  } catch (error) {
    // A missing language is valid: callers omit `language` and keep the current
    // behaviour, so a failed lookup must not surface as an error to the user.
    // It is logged, though: without this, "the lookup broke and the whole
    // feature silently did nothing" is indistinguishable from "this market
    // declares no language", which is how the first cut of this shipped
    // unlocalised with no signal at all.
    captureApolloError(
      LoggerErrorType.ApolloQuery,
      'resultsLanguages',
      error as ApolloError
    )
    return undefined
  }
}

/**
 * The language codes a survey's results, filters and quotas can be requested in.
 *
 * fd-core-responses resolves question text per field, and standard audience
 * questions come from a shared English-canonical library — so a German survey
 * renders English audience labels unless the results endpoints are told which
 * language to resolve to. The codes come from the survey's own fieldwork
 * (`languageAllocations`, falling back to `locale.language`), which is the same
 * client state the audience builder's language switch is driven from, rather
 * than a service the results pages otherwise never call.
 *
 * Several surveyIds are accepted, for a view with no single survey of its own:
 * the result is the union across those surveys, led by the first of the given
 * ids whose fieldwork resolved.
 */
export const useResultsLanguages = (surveyIds: string[]): ResultsLanguages => {
  const client = useApolloClient()
  const [languagesBySurveyId, setLanguagesBySurveyId] = useState<
    Map<string, MarketLanguages>
  >(new Map())
  // Which id set `languagesBySurveyId` describes, rather than a `loading`
  // boolean: a boolean initialised to `false` reads as "settled" on the first
  // render, before the effect has run, which is exactly when callers decide
  // whether to fire their results request.
  const [settledKey, setSettledKey] = useState<string>()

  // Keyed on the joined ids so a caller re-creating the array each render (the
  // common `[surveyId]` case) doesn't re-request on every render.
  const surveyIdsKey = surveyIds.join(',')
  const loading = surveyIdsKey !== '' && settledKey !== surveyIdsKey

  useEffect(() => {
    const ids = surveyIdsKey === '' ? [] : surveyIdsKey.split(',')
    if (ids.length === 0) {
      setLanguagesBySurveyId(new Map())
      return
    }

    let cancelled = false

    // fetchMarketLanguages swallows its own failures, so this never rejects.
    Promise.all(ids.map((id) => fetchMarketLanguages(client, id))).then(
      (results) => {
        if (cancelled) return
        const resolved = new Map<string, MarketLanguages>()
        results.forEach((result, index) => {
          if (result) resolved.set(ids[index], result)
        })
        setLanguagesBySurveyId(resolved)
        setSettledKey(surveyIdsKey)
      }
    )

    return () => {
      cancelled = true
    }
  }, [surveyIdsKey, client])

  return useMemo(() => {
    const ids = surveyIdsKey === '' ? [] : surveyIdsKey.split(',')
    const availableLanguages: string[] = []
    // The first market that resolved, not ids[0]: whatever leads the dropdown
    // has to be the code actually sent, or the selector shows a language the
    // results were not resolved in.
    let primaryLanguage: string | undefined

    for (const id of ids) {
      const marketLanguages = languagesBySurveyId.get(id)
      if (!marketLanguages) continue
      primaryLanguage ??= marketLanguages.primary
      for (const language of marketLanguages.languages) {
        if (!availableLanguages.includes(language)) {
          availableLanguages.push(language)
        }
      }
    }

    return {
      primaryLanguage,
      availableLanguages,
      loading
    }
  }, [surveyIdsKey, languagesBySurveyId, loading])
}
